import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import {
  Activity,
  BookOpen,
  Bug,
  CalendarRange,
  ExternalLink,
  Filter,
  GitBranch,
  LoaderCircle,
  Newspaper,
  RefreshCw,
  Search,
  Shield,
  Target,
  Waypoints,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

type NodeKind = 'actor' | 'campaign' | 'technique' | 'software' | 'cve';

interface ActorGraphInput {
  id: string;
  name: string;
  mitreId: string;
}

interface GraphNode {
  id: string;
  kind: NodeKind;
  label: string;
  secondary: string;
  description: string;
  stixId: string | null;
  externalId: string | null;
  sourceIds: string[];
  metadata: Record<string, unknown>;
}

interface GraphEdge {
  id: string;
  source: string;
  target: string;
  relationship: string;
  derived: boolean;
  description: string;
}

interface GraphResponse {
  ok: true;
  actor: ActorGraphInput;
  nodes: GraphNode[];
  edges: GraphEdge[];
  summary: { campaigns: number; techniques: number; software: number; cves: number; nodes: number; edges: number; truncated: boolean };
}

interface CampaignRecord {
  id: string;
  stixId: string;
  externalId: string;
  name: string;
  description: string;
  firstSeen: string | null;
  lastSeen: string | null;
  modifiedAt: string | null;
  objective: string;
  aliases: string[];
  techniqueCount: number;
  softwareCount: number;
  references: Array<{ sourceName: string; externalId: string; url: string; description: string }>;
}

interface CampaignResponse {
  ok: true;
  actor: ActorGraphInput;
  campaigns: CampaignRecord[];
  summary: { total: number };
}

type FeedKind = 'microsoft' | 'campaign' | 'kev' | 'actor_update' | 'source' | 'kloudynet_ti';
interface FeedItem {
  id: string;
  kind: FeedKind;
  title: string;
  summary: string;
  publisher: string;
  timestamp: string | null;
  url: string;
  severity: 'info' | 'important' | 'high';
  tags: string[];
  metadata: Record<string, unknown>;
}
interface FeedResponse {
  ok: true;
  actor: ActorGraphInput;
  items: FeedItem[];
  summary: { total: number };
}

type Phase3Tab = 'graph' | 'campaigns' | 'feed';

const GRAPH_W = 1120;
const GRAPH_H = 650;
const CX = GRAPH_W / 2;
const CY = GRAPH_H / 2;

function clean(value: unknown): string { return String(value ?? '').trim(); }
function fmt(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
function short(value: string, max = 68): string { return value.length <= max ? value : `${value.slice(0, max - 1)}…`; }

function kindLabel(kind: NodeKind): string {
  if (kind === 'technique') return 'ATT&CK / TTP';
  if (kind === 'software') return 'Malware / Tool';
  if (kind === 'cve') return 'CVE';
  if (kind === 'campaign') return 'Campaign';
  return 'Threat Actor';
}

function kindStyle(kind: NodeKind): { fill: string; stroke: string; text: string } {
  if (kind === 'actor') return { fill: '#083344', stroke: '#22d3ee', text: '#ecfeff' };
  if (kind === 'campaign') return { fill: '#2e1065', stroke: '#a78bfa', text: '#f5f3ff' };
  if (kind === 'technique') return { fill: '#082f49', stroke: '#38bdf8', text: '#e0f2fe' };
  if (kind === 'software') return { fill: '#052e16', stroke: '#34d399', text: '#ecfdf5' };
  return { fill: '#451a03', stroke: '#fbbf24', text: '#fffbeb' };
}

function sectorPositions(count: number, startDeg: number, endDeg: number, inner = 205, outer = 292): Array<{ x: number; y: number }> {
  if (!count) return [];
  const firstCount = Math.ceil(count / 2);
  return Array.from({ length: count }, (_, index) => {
    const outerRing = index >= firstCount;
    const ringIndex = outerRing ? index - firstCount : index;
    const ringCount = outerRing ? count - firstCount : firstCount;
    const denominator = Math.max(1, ringCount - 1);
    const degree = startDeg + ((endDeg - startDeg) * ringIndex) / denominator;
    const angle = (degree * Math.PI) / 180;
    const radius = outerRing ? outer : inner;
    return { x: CX + Math.cos(angle) * radius, y: CY + Math.sin(angle) * radius };
  });
}

function layoutGraph(nodes: GraphNode[]): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  const actor = nodes.find((node) => node.kind === 'actor');
  if (actor) positions.set(actor.id, { x: CX, y: CY });
  const groups: Array<{ kind: NodeKind; start: number; end: number }> = [
    { kind: 'campaign', start: 205, end: 335 },
    { kind: 'software', start: -25, end: 55 },
    { kind: 'cve', start: 58, end: 125 },
    { kind: 'technique', start: 128, end: 218 },
  ];
  for (const group of groups) {
    const items = nodes.filter((node) => node.kind === group.kind);
    const points = sectorPositions(items.length, group.start, group.end);
    items.forEach((node, index) => positions.set(node.id, points[index] ?? { x: CX, y: CY }));
  }
  return positions;
}

function edgePath(source: { x: number; y: number }, target: { x: number; y: number }): string {
  const midX = (source.x + target.x) / 2;
  const midY = (source.y + target.y) / 2;
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
  const curve = Math.min(34, len * 0.09);
  const cx = midX - (dy / len) * curve;
  const cy = midY + (dx / len) * curve;
  return `M ${source.x.toFixed(1)} ${source.y.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${target.x.toFixed(1)} ${target.y.toFixed(1)}`;
}

function relationTone(rel: string): string {
  const value = rel.toLowerCase();
  if (value.includes('use')) return '#34d399';
  if (value.includes('attribute')) return '#a78bfa';
  if (value.includes('target')) return '#fb7185';
  if (value.includes('vulnerab')) return '#fbbf24';
  return '#38bdf8';
}

export function ThreatLandscapeIntelligenceGraph({ actor }: { actor: ActorGraphInput }) {
  const { canCapability } = useAuth();
  const canGraph = canCapability('threat_intel', 'view_intelligence_graph');
  const canCampaigns = canCapability('threat_intel', 'view_actor_campaigns');
  const canFeed = canCapability('threat_intel', 'view_threat_feed');
  const allowedTabs = useMemo<Phase3Tab[]>(() => [
    ...(canGraph ? ['graph' as const] : []),
    ...(canCampaigns ? ['campaigns' as const] : []),
    ...(canFeed ? ['feed' as const] : []),
  ], [canCampaigns, canFeed, canGraph]);
  const [tab, setTab] = useState<Phase3Tab>(allowedTabs[0] ?? 'graph');
  const [graph, setGraph] = useState<GraphResponse | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignResponse | null>(null);
  const [feed, setFeed] = useState<FeedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [graphSearch, setGraphSearch] = useState('');
  const [enabledKinds, setEnabledKinds] = useState<Set<NodeKind>>(new Set(['actor', 'campaign', 'technique', 'software', 'cve']));
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [feedKind, setFeedKind] = useState<'all' | FeedKind>('all');

  useEffect(() => {
    if (!allowedTabs.includes(tab)) setTab(allowedTabs[0] ?? 'graph');
  }, [allowedTabs, tab]);

  useEffect(() => {
    setGraph(null); setCampaigns(null); setFeed(null); setError(''); setSelectedNodeId(''); setSelectedCampaignId(''); setGraphSearch(''); setFeedKind('all');
  }, [actor.id]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      try {
        if (tab === 'graph' && canGraph && !graph) {
          const result = await apiFetch<GraphResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.id)}/graph`);
          if (!cancelled) { setGraph(result); setSelectedNodeId(result.nodes.find((node) => node.kind === 'actor')?.id ?? result.nodes[0]?.id ?? ''); }
        }
        if (tab === 'campaigns' && canCampaigns && !campaigns) {
          const result = await apiFetch<CampaignResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.id)}/campaigns`);
          if (!cancelled) { setCampaigns(result); setSelectedCampaignId(result.campaigns[0]?.id ?? ''); }
        }
        if (tab === 'feed' && canFeed && !feed) {
          const result = await apiFetch<FeedResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.id)}/feed?limit=100`);
          if (!cancelled) setFeed(result);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Phase 3 intelligence view failed to load.');
      } finally { if (!cancelled) setLoading(false); }
    }
    void load();
    return () => { cancelled = true; };
  }, [actor.id, canCampaigns, canFeed, canGraph, campaigns, feed, graph, tab]);

  if (!allowedTabs.length) {
    return <div className="rounded-2xl border border-amber-400/25 bg-amber-500/10 px-4 py-3 text-xs text-amber-100"><b>Phase 3 Intelligence Graph is permission controlled.</b> Grant one or more of <code>view_intelligence_graph</code>, <code>view_actor_campaigns</code> or <code>view_threat_feed</code> in Permissions Master.</div>;
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-violet-400/20 bg-gradient-to-br from-slate-950 via-slate-950 to-violet-950/25 shadow-xl">
      <div className="border-b border-white/10 px-5 py-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-violet-300"><GitBranch size={15} /> Phase 3 · Intelligence Graph</div>
            <h4 className="mt-2 text-xl font-black text-white">Explore the intelligence around {actor.name}</h4>
            <p className="mt-1 max-w-4xl text-xs leading-5 text-blue-100/60">Server-side STIX relationships are rendered as a bounded deterministic graph: actor ↔ campaigns ↔ ATT&CK/TTPs ↔ malware/tooling plus source-backed CVEs. No browser-side ATT&CK bundle and no continuous force-physics loop.</p>
          </div>
          <div className="inline-flex self-start rounded-xl border border-white/10 bg-white/5 p-1">
            {canGraph && <TabButton active={tab === 'graph'} onClick={() => setTab('graph')} icon={GitBranch} label="Graph" />}
            {canCampaigns && <TabButton active={tab === 'campaigns'} onClick={() => setTab('campaigns')} icon={CalendarRange} label="Campaigns" />}
            {canFeed && <TabButton active={tab === 'feed'} onClick={() => setTab('feed')} icon={Newspaper} label="Feed" />}
          </div>
        </div>
      </div>

      {error && <div className="m-4 rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-100">{error}</div>}
      {loading && <div className="flex items-center gap-2 px-5 py-6 text-sm text-blue-100/65"><LoaderCircle size={16} className="animate-spin text-violet-300" /> Loading {tab === 'graph' ? 'relationship graph' : tab === 'campaigns' ? 'campaign timeline' : 'intelligence feed'}…</div>}
      {!loading && tab === 'graph' && canGraph && graph && <GraphView graph={graph} search={graphSearch} setSearch={setGraphSearch} enabledKinds={enabledKinds} setEnabledKinds={setEnabledKinds} selectedNodeId={selectedNodeId} setSelectedNodeId={setSelectedNodeId} />}
      {!loading && tab === 'campaigns' && canCampaigns && campaigns && <CampaignView response={campaigns} selectedId={selectedCampaignId} setSelectedId={setSelectedCampaignId} />}
      {!loading && tab === 'feed' && canFeed && feed && <FeedView response={feed} kind={feedKind} setKind={setFeedKind} />}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof GitBranch; label: string }) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-black ${active ? 'bg-violet-400 text-slate-950' : 'text-blue-100 hover:bg-white/10'}`}><Icon size={13} />{label}</button>;
}

function GraphView({ graph, search, setSearch, enabledKinds, setEnabledKinds, selectedNodeId, setSelectedNodeId }: {
  graph: GraphResponse;
  search: string;
  setSearch: (value: string) => void;
  enabledKinds: Set<NodeKind>;
  setEnabledKinds: Dispatch<SetStateAction<Set<NodeKind>>>;
  selectedNodeId: string;
  setSelectedNodeId: (value: string) => void;
}) {
  const actorNode = graph.nodes.find((node) => node.kind === 'actor');
  const needle = search.trim().toLowerCase();
  const visibleNodes = useMemo(() => graph.nodes.filter((node) => {
    if (node.kind === 'actor') return true;
    if (!enabledKinds.has(node.kind)) return false;
    if (!needle) return true;
    return [node.label, node.secondary, node.description].some((value) => value.toLowerCase().includes(needle));
  }), [enabledKinds, graph.nodes, needle]);
  const visibleIds = useMemo(() => new Set(visibleNodes.map((node) => node.id)), [visibleNodes]);
  const visibleEdges = useMemo(() => graph.edges.filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target)), [graph.edges, visibleIds]);
  const positions = useMemo(() => layoutGraph(visibleNodes), [visibleNodes]);
  const selectedNode = graph.nodes.find((node) => node.id === selectedNodeId) ?? actorNode ?? graph.nodes[0];
  const connectedIds = useMemo(() => {
    const ids = new Set<string>();
    if (!selectedNode) return ids;
    ids.add(selectedNode.id);
    visibleEdges.forEach((edge) => {
      if (edge.source === selectedNode.id) ids.add(edge.target);
      if (edge.target === selectedNode.id) ids.add(edge.source);
    });
    return ids;
  }, [selectedNode, visibleEdges]);
  const selectedEdges = selectedNode ? visibleEdges.filter((edge) => edge.source === selectedNode.id || edge.target === selectedNode.id) : [];

  function toggleKind(kind: NodeKind) {
    if (kind === 'actor') return;
    setEnabledKinds((current) => {
      const next = new Set(current);
      if (next.has(kind)) next.delete(kind); else next.add(kind);
      next.add('actor');
      return next;
    });
  }

  return (
    <div className="p-4">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div>
          <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-100/40" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter graph nodes…" className="w-full rounded-xl border border-white/10 bg-black/20 py-2 pl-9 pr-3 text-xs text-white outline-none focus:border-violet-400/40" /></div>
            <div className="flex flex-wrap gap-1.5">
              {(['campaign', 'technique', 'software', 'cve'] as NodeKind[]).map((kind) => <button key={kind} type="button" onClick={() => toggleKind(kind)} className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-black ${enabledKinds.has(kind) ? 'border-violet-400/30 bg-violet-500/15 text-violet-100' : 'border-white/10 bg-white/5 text-blue-100/45'}`}>{kindLabel(kind)} · {graph.nodes.filter((node) => node.kind === kind).length}</button>)}
              <button type="button" onClick={() => { setSearch(''); setEnabledKinds(new Set(['actor', 'campaign', 'technique', 'software', 'cve'])); if (actorNode) setSelectedNodeId(actorNode.id); }} className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-[10px] font-black text-blue-100 hover:bg-white/10"><RefreshCw size={11} /> Reset</button>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-violet-400/15 bg-[#020817]">
            <svg viewBox={`0 0 ${GRAPH_W} ${GRAPH_H}`} className="block h-auto w-full" role="img" aria-label={`Intelligence relationship graph for ${graph.actor.name}`}>
              <defs>
                <filter id="ks-phase3-glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                <radialGradient id="ks-phase3-bg"><stop offset="0" stopColor="#172554" stopOpacity="0.38" /><stop offset="0.48" stopColor="#111827" stopOpacity="0.14" /><stop offset="1" stopColor="#020817" stopOpacity="0" /></radialGradient>
              </defs>
              <rect width={GRAPH_W} height={GRAPH_H} fill="#020817" />
              <circle cx={CX} cy={CY} r="315" fill="url(#ks-phase3-bg)" stroke="#312e81" strokeOpacity="0.35" strokeDasharray="4 9" />
              <circle cx={CX} cy={CY} r="215" fill="none" stroke="#164e63" strokeOpacity="0.24" strokeDasharray="3 9" />
              {visibleEdges.map((edge) => {
                const source = positions.get(edge.source); const target = positions.get(edge.target);
                if (!source || !target) return null;
                const active = selectedNode ? edge.source === selectedNode.id || edge.target === selectedNode.id : false;
                return <path key={edge.id} d={edgePath(source, target)} fill="none" stroke={relationTone(edge.relationship)} strokeWidth={active ? 2.5 : 1.1} strokeOpacity={active ? 0.9 : 0.22} strokeDasharray={edge.derived ? '4 5' : active ? '7 5' : '3 6'} className="transition-all duration-200"><title>{edge.relationship}{edge.description ? ` · ${edge.description}` : ''}</title></path>;
              })}
              {visibleNodes.map((node) => {
                const point = positions.get(node.id); if (!point) return null;
                const style = kindStyle(node.kind);
                const active = node.id === selectedNode?.id;
                const related = !selectedNode || connectedIds.has(node.id);
                const opacity = related ? 1 : 0.42;
                if (node.kind === 'actor') return <g key={node.id} transform={`translate(${point.x} ${point.y})`} className="cursor-pointer" opacity={opacity} onClick={() => setSelectedNodeId(node.id)}><circle r={active ? 58 : 52} fill={style.fill} stroke={style.stroke} strokeWidth={active ? 4 : 2} filter="url(#ks-phase3-glow)" /><Target x={-14} y={-27} width={28} height={28} color={style.stroke} /><text y="14" textAnchor="middle" fontSize="15" fontWeight="800" fill={style.text}>{short(node.label, 18)}</text><text y="32" textAnchor="middle" fontSize="10" fill="#94a3b8">{node.secondary}</text></g>;
                const width = node.kind === 'technique' ? 142 : node.kind === 'cve' ? 150 : 158;
                return <g key={node.id} transform={`translate(${point.x} ${point.y})`} className="cursor-pointer" opacity={opacity} onClick={() => setSelectedNodeId(node.id)}><rect x={-width / 2} y="-27" width={width} height="54" rx={node.kind === 'cve' ? 8 : 14} fill={style.fill} stroke={style.stroke} strokeWidth={active ? 3 : 1.4} filter={active ? 'url(#ks-phase3-glow)' : undefined} /><text x="0" y="-4" textAnchor="middle" fontSize="11" fontWeight="800" fill={style.text}>{short(node.label, node.kind === 'cve' ? 22 : 21)}</text><text x="0" y="13" textAnchor="middle" fontSize="8.5" fill="#94a3b8">{short(node.secondary, 25)}</text></g>;
              })}
            </svg>
            <div className="pointer-events-none absolute left-3 top-3 rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-[10px] text-blue-100/60 backdrop-blur"><b className="text-violet-200">{visibleNodes.length}</b> nodes · <b className="text-cyan-200">{visibleEdges.length}</b> relationships{graph.summary.truncated ? ' · bounded view' : ''}</div>
            <div className="pointer-events-none absolute bottom-3 left-3 flex flex-wrap gap-2 rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-[10px] backdrop-blur"><LegendDot label="Campaign" color="#a78bfa" /><LegendDot label="TTP" color="#38bdf8" /><LegendDot label="Malware / Tool" color="#34d399" /><LegendDot label="CVE" color="#fbbf24" /></div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          {selectedNode ? <>
            <div className="text-[10px] font-black uppercase tracking-[0.15em] text-violet-300">Selected node · {kindLabel(selectedNode.kind)}</div>
            <h5 className="mt-2 text-lg font-black text-white">{selectedNode.label}</h5>
            <div className="mt-1 text-xs font-mono text-cyan-200">{selectedNode.secondary || selectedNode.externalId || '-'}</div>
            <p className="mt-3 text-xs leading-5 text-blue-100/65">{selectedNode.description || 'No additional description in the synchronized object.'}</p>
            <div className="mt-4 grid grid-cols-2 gap-2"><MiniMetric label="Relationships" value={selectedEdges.length} /><MiniMetric label="Sources" value={selectedNode.sourceIds.length} /></div>
            {selectedEdges.length > 0 && <div className="mt-4"><div className="text-[10px] font-black uppercase tracking-wide text-blue-100/45">Connected by</div><div className="mt-2 space-y-1.5">{selectedEdges.slice(0, 12).map((edge) => <div key={edge.id} className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-2 text-[11px] text-blue-100/65"><b className="text-cyan-200">{edge.relationship}</b>{edge.derived ? ' · normalized' : ' · STIX relationship'}</div>)}</div></div>}
            {Object.keys(selectedNode.metadata ?? {}).length > 0 && <details className="mt-4 rounded-xl border border-white/10 bg-black/15 p-3"><summary className="cursor-pointer text-[10px] font-black uppercase tracking-wide text-blue-100/55">Object metadata</summary><pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap break-words text-[10px] leading-4 text-blue-100/55">{JSON.stringify(selectedNode.metadata, null, 2)}</pre></details>}
          </> : <div className="text-xs text-blue-100/50">Select a node to inspect its relationships.</div>}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ label, color }: { label: string; color: string }) { return <span className="inline-flex items-center gap-1.5 text-blue-100/60"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />{label}</span>; }
function MiniMetric({ label, value }: { label: string; value: number }) { return <div className="rounded-xl border border-white/10 bg-black/20 p-2.5"><div className="text-[9px] font-black uppercase tracking-wide text-blue-100/40">{label}</div><div className="mt-1 text-lg font-black text-white">{value}</div></div>; }

function CampaignView({ response, selectedId, setSelectedId }: { response: CampaignResponse; selectedId: string; setSelectedId: (value: string) => void }) {
  const selected = response.campaigns.find((campaign) => campaign.id === selectedId) ?? response.campaigns[0] ?? null;
  return <div className="grid gap-4 p-4 xl:grid-cols-[360px_minmax(0,1fr)]">
    <div className="max-h-[640px] space-y-2 overflow-auto pr-1">
      {response.campaigns.map((campaign) => <button key={campaign.id} type="button" onClick={() => setSelectedId(campaign.id)} className={`w-full rounded-xl border p-3 text-left ${selected?.id === campaign.id ? 'border-violet-400/40 bg-violet-500/15' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}><div className="flex items-start justify-between gap-2"><div className="font-black text-white">{campaign.name}</div><span className="rounded-md border border-white/10 px-1.5 py-0.5 font-mono text-[9px] text-blue-100/55">{campaign.externalId || 'STIX'}</span></div><div className="mt-1 text-[10px] text-blue-100/50">Last seen {fmt(campaign.lastSeen ?? campaign.modifiedAt)}</div><div className="mt-2 flex gap-2 text-[9px] font-bold text-blue-100/50"><span>{campaign.techniqueCount} TTPs</span><span>{campaign.softwareCount} software</span></div></button>)}
      {!response.campaigns.length && <div className="rounded-xl border border-dashed border-white/10 p-4 text-xs text-blue-100/45">No ATT&CK campaign object is directly related to this actor in the current synchronized dataset.</div>}
    </div>
    {selected && <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><div className="flex flex-wrap items-center gap-2"><span className="rounded-full border border-violet-400/30 bg-violet-500/10 px-2.5 py-1 text-[10px] font-black text-violet-200">ATT&CK Campaign</span>{selected.externalId && <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] text-blue-100/60">{selected.externalId}</span>}</div><h5 className="mt-3 text-xl font-black text-white">{selected.name}</h5><p className="mt-3 text-xs leading-6 text-blue-100/65">{selected.description || 'No campaign description is present in the synchronized ATT&CK object.'}</p>{selected.objective && <div className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-500/10 p-3"><div className="text-[10px] font-black uppercase tracking-wide text-cyan-200">Objective</div><div className="mt-1 text-xs leading-5 text-cyan-50/80">{selected.objective}</div></div>}<div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4"><CampaignMetric label="First seen" value={fmt(selected.firstSeen)} /><CampaignMetric label="Last seen" value={fmt(selected.lastSeen)} /><CampaignMetric label="ATT&CK / TTP" value={String(selected.techniqueCount)} /><CampaignMetric label="Malware / tools" value={String(selected.softwareCount)} /></div>{selected.aliases.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{selected.aliases.map((alias) => <span key={alias} className="rounded-lg border border-white/10 bg-black/20 px-2 py-1 text-[10px] text-blue-100/60">{alias}</span>)}</div>}<div className="mt-5"><div className="flex items-center gap-2 text-xs font-black text-white"><BookOpen size={14} className="text-cyan-300" /> Source references</div><div className="mt-2 grid gap-2 lg:grid-cols-2">{selected.references.filter((ref) => ref.url).map((ref, index) => <a key={`${ref.url}-${index}`} href={ref.url} target="_blank" rel="noreferrer" className="rounded-xl border border-white/10 bg-black/20 p-3 hover:border-cyan-400/30"><div className="flex justify-between gap-2"><div className="text-xs font-bold text-blue-50">{ref.sourceName || 'Source'}</div><ExternalLink size={12} className="text-blue-100/40" /></div><div className="mt-1 font-mono text-[9px] text-cyan-200/70">{ref.externalId}</div></a>)}</div></div></div>}
  </div>;
}
function CampaignMetric({ label, value }: { label: string; value: string }) { return <div className="rounded-xl border border-white/10 bg-black/20 p-3"><div className="text-[9px] font-black uppercase tracking-wide text-blue-100/40">{label}</div><div className="mt-1 text-xs font-bold text-white">{value}</div></div>; }

function FeedView({ response, kind, setKind }: { response: FeedResponse; kind: 'all' | FeedKind; setKind: (value: 'all' | FeedKind) => void }) {
  const kinds = useMemo(() => ['all', ...Array.from(new Set(response.items.map((item) => item.kind)))] as Array<'all' | FeedKind>, [response.items]);
  const items = kind === 'all' ? response.items : response.items.filter((item) => item.kind === kind);
  return <div className="p-4"><div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"><div><div className="flex items-center gap-2 text-sm font-black text-white"><Newspaper size={15} className="text-violet-300" /> Intelligence stream</div><div className="mt-1 text-xs text-blue-100/55">Chronological source-backed context for {response.actor.name}: Microsoft research, ATT&CK campaigns, CISA KEV and synchronized profile updates.</div></div><label className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-blue-100/60"><Filter size={12} /><span>Feed type</span><select value={kind} onChange={(e) => setKind(e.target.value as 'all' | FeedKind)} className="bg-transparent font-bold text-white outline-none">{kinds.map((item) => <option key={item} value={item}>{item === 'all' ? 'All' : feedKindLabel(item)}</option>)}</select></label></div><div className="relative space-y-3 before:absolute before:bottom-2 before:left-[17px] before:top-2 before:w-px before:bg-violet-400/20">{items.map((item) => <FeedCard key={item.id} item={item} />)}{!items.length && <div className="rounded-xl border border-dashed border-white/10 p-4 text-xs text-blue-100/45">No feed items match this filter.</div>}</div></div>;
}
function feedKindLabel(kind: FeedKind): string { if (kind === 'microsoft') return 'Microsoft TI'; if (kind === 'campaign') return 'ATT&CK Campaign'; if (kind === 'kev') return 'CISA KEV'; if (kind === 'actor_update') return 'Actor Update'; if (kind === 'kloudynet_ti') return 'Kloudynet TI'; return 'Source'; }
function FeedCard({ item }: { item: FeedItem }) {
  const Icon = item.kind === 'microsoft' ? Newspaper : item.kind === 'campaign' ? Waypoints : item.kind === 'kev' ? Bug : item.kind === 'actor_update' ? Shield : Activity;
  const tone = item.severity === 'high' ? 'border-rose-400/25 bg-rose-500/10' : item.severity === 'important' ? 'border-amber-400/20 bg-amber-500/5' : 'border-white/10 bg-white/5';
  return <div className="relative pl-10"><div className="absolute left-[7px] top-4 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-violet-400/30 bg-slate-950"><Icon size={10} className="text-violet-300" /></div><div className={`rounded-xl border p-4 ${tone}`}><div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between"><div><div className="text-[10px] font-black uppercase tracking-wide text-violet-200">{feedKindLabel(item.kind)} · {item.publisher}</div><div className="mt-1 text-sm font-black text-white">{item.title}</div></div><div className="shrink-0 text-[10px] text-blue-100/45">{fmt(item.timestamp)}</div></div>{item.summary && <p className="mt-2 text-xs leading-5 text-blue-100/60">{item.summary}</p>}<div className="mt-3 flex flex-wrap items-center gap-2">{item.tags.slice(0, 8).map((tag) => <span key={tag} className="rounded-md border border-white/10 bg-black/20 px-2 py-1 text-[9px] text-blue-100/55">{tag}</span>)}{item.url && <a href={item.url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-cyan-200 hover:text-cyan-100">Open source <ExternalLink size={11} /></a>}</div></div></div>;
}
